import {useEffect, useId} from 'react';
import {ErrorNotice, Loading, dateTime} from './components';
import type {hermesActions} from './lib/store';
import type {AccountVirtualMachineStatus, HermesState} from './lib/types';
import './virtual-machine-settings.css';

const stateLabels: Record<AccountVirtualMachineStatus['state'], string> = {
  unallocated: 'Not allocated', running: 'Running', stopped: 'Stopped', starting: 'Starting',
  stopping: 'Stopping', paused: 'Paused', crashed: 'Crashed', unavailable: 'Unavailable',
};
const maintenanceLabels: Record<NonNullable<AccountVirtualMachineStatus['maintenance']>, string> = {
  ready: 'Idle', updating: 'Updating Linux', 'update-failed': 'Linux update failed',
  'rolling-back': 'Restoring previous OS', 'rollback-failed': 'OS restore failed', 'os-changing': 'Changing operating system',
};

export function VirtualMachineSettings({state, actions}: {state: HermesState; actions: typeof hermesActions}) {
  const titleId = useId();
  const accountId = state.session.data?.accountId;
  const resource = state.accountVirtualMachine;
  const status = resource.data;
  const operatingSystem = status?.operatingSystem;
  const recovering = status?.recovery.state === 'running';
  const busy = state.operation.label !== null;
  const clearingData = state.accountDataReset.data?.state === 'running' || state.accountDataReset.data?.state === 'failed';

  useEffect(() => {
    if (!accountId) return;
    let pending: AbortController | null = null;
    const read = () => {
      if (document.visibilityState !== 'visible' || pending !== null) return;
      const controller = new AbortController();
      pending = controller;
      void actions.refreshAccountVirtualMachine(controller.signal).catch(() => undefined)
        .finally(() => {if (pending === controller) pending = null;});
    };
    read();
    const interval = window.setInterval(read, recovering ? 3000 : 10000);
    document.addEventListener('visibilitychange', read);
    window.addEventListener('focus', read);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', read);
      window.removeEventListener('focus', read);
      pending?.abort();
    };
  }, [accountId, actions, recovering]);

  return <section className="settings-section settings-virtual-machine" aria-labelledby={titleId}>
    <div className="settings-description"><h2 id={titleId}>Virtual machine</h2>
      <p>Your private Linux environment runs your team and applications.</p>
      <p>Linux updates are managed by your administrator.</p>
    </div>
    <div className="virtual-machine-status">
      <Loading active={resource.status === 'loading' || recovering}/>
      <ErrorNotice error={resource.error}/>
      <ErrorNotice error={status?.recovery.error ? {name: 'VmRecoveryError', message: status.recovery.error} : null}/>
      {status?.backend === 'container' && <p className="field-help">This account has not been migrated to a virtual machine.</p>}
      {status?.backend === 'vm' && <>
        <dl className="virtual-machine-details">
          <div><dt>VM status</dt><dd>{stateLabels[status.state]}</dd></div>
          {status.maintenance !== null && <div><dt>Maintenance</dt><dd>{maintenanceLabels[status.maintenance]}</dd></div>}
          {recovering && <div><dt>Recovery</dt><dd>Restoring previous OS</dd></div>}
          <div><dt>Operating system</dt><dd>{operatingSystem?.name ?? 'Unavailable'}</dd></div>
          {operatingSystem?.versionId && <div><dt>OS release</dt><dd>{operatingSystem.versionId}</dd></div>}
          <div><dt>Linux kernel</dt><dd>{operatingSystem?.kernelRelease ?? 'Unavailable'}</dd></div>
        </dl>
        {status.state === 'unallocated' && <p className="field-help">Your virtual machine has not been allocated yet.</p>}
        {!operatingSystem && status.state !== 'unallocated' && <p className="field-help">{status.guestStatus === 'not-running'
          ? 'The Linux version will be available when your virtual machine is running.'
          : 'The operating system details could not be read.'}</p>}
        {status.recovery.available && <div className="virtual-machine-recovery">
          <p className="field-help">Restore the previous operating system after an interrupted or failed update. Your separate files disk is retained.</p>
          <button className="button" type="button" disabled={busy || recovering || clearingData}
            onClick={() => void actions.recoverAccountVirtualMachine().catch(() => undefined)}>Recover VM</button>
        </div>}
        {!status.recovery.available && !recovering && (status.maintenance === 'update-failed' || status.maintenance === 'rollback-failed') &&
          <p className="field-help">Contact your administrator to recover your operating system.</p>}
      </>}
      {status && <p className="field-help virtual-machine-observed">Checked <time dateTime={status.observedAt}>{dateTime(status.observedAt)}</time></p>}
    </div>
  </section>;
}
